import React, { FC, memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GeometryType, LayerReferenceConfigurationDc } from "@evergis/api";
import { Divider, Icon, IconToggleButton, LinearProgress } from "@evergis/ui";

import {
  BackCard,
  Card,
  CardsContainer,
  FeatureNumber,
  FeaturesPagination,
  FeatureTools,
  FeatureToolsDividerContainer,
  Main,
  StatCount,
} from "../styled";

import { FeatureCardProvider } from "./FeatureCardHeader/FeatureCardContext";
import { FeatureConfigAttributes } from "./Attributes/FeatureConfigAttributes";
import { EditableFeatureAttributes } from "./Attributes/EditableFeatureAttributes";
import { HandBookRecords } from "./Attributes/HandBookRecords";
import { FeatureCardHeader } from "./FeatureCardHeader/FeatureCardHeader";
import { LayerStatistics } from "./LayerStatistics";
import { LayerPickMenu } from "./LayerPickMenu";
import { FeatureCardContextMenu } from "./FeatureCardContextMenu";

import { getBottomHeight, getBottomState } from "../../../ducks/layout/selectors";
import { getPreviousFeatureReference, getSelectedTabId } from "../../../ducks/showFeature/selectors";
import { getFeatureAttributes } from "../utils/getFeatureAttributes";
import { getSelectedFeaturesTotalCounts } from "../../../ducks/selectFeatures/selectors";
import {
  setSelectedTabId as setSelectedTabIdAction,
  nextPage as nextPageAction,
  prevPage as prevPageAction,
  changePage as changePageAction,
} from "../../../ducks/showFeature/showFeature";
import { useFeature } from "../../../hooks/feature/useFeature";
import { useLayerEditable } from "../../../hooks/layer/useLayerEditable";
import { useToggle } from "../../../hooks/useToggle";
import { useAttributesInHistory } from "../hooks/useAttributesHistory";
import { useFeatureAttributes } from "../hooks/useFeatureAttributes";
import { useFeatureCard } from "../hooks/useFeatureCard";
import { useRasterLayer } from "../hooks/useRasterLayer";
import { useTempLayer } from "../../../hooks/layer/useTempLayer";
import { useEditUpdateEffect } from "../hooks/useEditUpdateEffect";
import { useReferenceLayerInfos } from "../hooks/useReferenceLayerInfos";
import { useFeatureDataSources } from "../hooks/useFeatureDataSources";
import { useAction } from "../../../hooks/useAction";
import { useExpandableContainers } from "../hooks/useExpandableContainers";
import { useFeatureDataSourceFilters } from "../hooks/useFeatureDataSourceFilters";
import { useSelectFeaturesStatisticsActive } from "../../../hooks/selectFeatures/useSelectFeaturesStatisticsActive";
import { useSelectFeaturesGeometryMasking } from "../../../hooks/selectFeatures/useSelectFeaturesGeometryMasking";

import { FeatureCardMode } from "../types";
import { FeatureCardProps } from "./types";
import { SelectedFeatureDc } from "../../../ducks/selectFeatures/types";

// eslint-disable-next-line max-lines-per-function
export const FeatureCard: FC<FeatureCardProps> = memo(({ feature, layerName, mode, loading }) => {
  const { t } = useTranslation("featureCard");
  const editOnly = mode === FeatureCardMode.Create;
  const { postgresLayer, geometryType, featureName, featureLayer, coordinates } = useFeature(feature, null, layerName);
  const bottomState = useSelector(getBottomState);
  const bottomHeight: number = useSelector(getBottomHeight);
  const previousFeatureReference = useSelector(getPreviousFeatureReference);
  const selectedTabId = useSelector(getSelectedTabId);
  const setSelectedTabId = useAction(setSelectedTabIdAction);
  const nextPage = useAction(nextPageAction);
  const prevPage = useAction(prevPageAction);
  const changePage = useAction(changePageAction);
  const { getTempLayerInfo } = useTempLayer();
  const tempLayerInfo = getTempLayerInfo(feature?.layer || layerName);
  const currentLayerName = tempLayerInfo?.name || feature?.layer || layerName;
  const layerInfo = tempLayerInfo || postgresLayer;
  const [isEdit, toggleEditMode] = useToggle(editOnly);
  const [{ itemsCount, currentIndex }, { closeCard, zoomToFeature, onNext, onPrev, resetSelected }] = useFeatureCard();
  const [isFeatureEditable, hasCopyRights] = useLayerEditable(currentLayerName);
  const isMultipoint = geometryType === GeometryType.Multipoint;
  const attributes = useMemo(() => getFeatureAttributes(feature as SelectedFeatureDc, layerInfo), [feature, layerInfo]);
  const isRaster = useRasterLayer(featureLayer);
  const [selectFeaturesStatisticsActive, setSelectFeaturesStatisticsActive] = useSelectFeaturesStatisticsActive();
  const [expandedContainers, expandContainer] = useExpandableContainers();
  const [filters, changeFilters] = useFeatureDataSourceFilters();
  const [statCount, setStatCount] = useState<number | undefined>();
  const [handBookReference, setHandBookReference] = useState<LayerReferenceConfigurationDc>();
  const totalCounts = useSelector(getSelectedFeaturesTotalCounts);
  const [selectFeaturesGeometryMasking, setSelectFeaturesGeometryMasking] = useSelectFeaturesGeometryMasking();

  const [attributesState, { updateAttribute, saveAttributes, storeAttributes }] = useFeatureAttributes(
    feature,
    isEdit,
    mode,
    layerName,
    resetSelected,
  );

  useEditUpdateEffect(!isRaster && isEdit, { geometryType, layerName: currentLayerName, geometry: coordinates });
  useAttributesInHistory(attributes, isEdit);
  useReferenceLayerInfos(layerInfo?.configuration?.cardConfiguration?.dataSources);
  useFeatureDataSources(layerInfo, attributes, filters);

  useEffect(() => {
    if (previousFeatureReference) {
      setHandBookReference(undefined);
    }
  }, [feature?.id, feature?.layer, mode]);

  return (
    <CardsContainer>
      {(!loading || selectFeaturesStatisticsActive) && (
        <BackCard>
          {!selectFeaturesStatisticsActive ? (
            <FeaturesPagination
              currentPage={currentIndex === -1 ? 1 : currentIndex + 1}
              pageCount={itemsCount ?? 1}
              disabled={(isEdit && !isMultipoint) || selectFeaturesStatisticsActive}
              onPrev={onPrev}
              onNext={onNext}
            >
              <FeatureNumber onClick={() => zoomToFeature(feature)}>
                {currentIndex === -1 ? 1 : currentIndex + 1}
              </FeatureNumber>
              {`${t("objectOf")} ${totalCounts?.[currentLayerName] ?? 1}`}
            </FeaturesPagination>
          ) : (
            <StatCount>
              {statCount !== undefined && (
                <>
                  {t("objects")}: {statCount}
                </>
              )}
            </StatCount>
          )}
          <FeatureTools>
            <IconToggleButton
              isSelected={selectFeaturesStatisticsActive}
              onClick={() => setSelectFeaturesStatisticsActive(!selectFeaturesStatisticsActive)}
              disabled={isEdit || currentIndex === -1}
            >
              <Icon kind="stats" />
            </IconToggleButton>
            <IconToggleButton
              isSelected={selectFeaturesGeometryMasking}
              onClick={() => setSelectFeaturesGeometryMasking(!selectFeaturesGeometryMasking)}
              disabled={isEdit || currentIndex === -1}
            >
              <Icon kind="preview" />
            </IconToggleButton>
            <FeatureToolsDividerContainer>
              <Divider vertical />
            </FeatureToolsDividerContainer>
            <LayerPickMenu disabled={isEdit || currentIndex === -1} />
            <FeatureCardContextMenu layerInfo={layerInfo} disabled={isEdit || currentIndex === -1} />
          </FeatureTools>
        </BackCard>
      )}
      {!selectFeaturesStatisticsActive && (
        <Card bottomState={bottomState} bottomHeight={bottomHeight}>
          {loading && !editOnly && <LinearProgress />}
          <FeatureCardProvider
            settings={{
              selectedTabId,
              attributes,
              itemsCount,
              featureLayer,
              layerInfo,
              featureName,
              feature,
              geometryType,
              coordinates,
              handBookReference,
              editOnly,
              isEdit,
              isRaster,
              isFeatureEditable,
              hasCopyRights,
              expandedContainers,
              filters,
              zoomToFeature,
              closeCard,
              expandContainer,
              changeFilters,
              toggleEditMode,
              saveAttributes,
              setHandBookReference,
              setSelectedTabId,
              nextPage,
              prevPage,
              changePage,
            }}
          >
            <FeatureCardHeader />
            {!handBookReference && (
              <Main padding={isEdit ? "1rem 0.5rem 1rem 1rem" : "1rem 0.5rem 1rem 1.5rem"}>
                {isEdit || editOnly ? (
                  <EditableFeatureAttributes
                    layerInfo={layerInfo}
                    attributes={attributesState}
                    updateAttribute={updateAttribute}
                    storeAttributes={storeAttributes}
                  />
                ) : (
                  <FeatureConfigAttributes />
                )}
              </Main>
            )}
            {handBookReference && (
              <HandBookRecords
                layerReference={handBookReference}
                attributes={attributes}
                feature={feature}
                previousFeatureReference={previousFeatureReference}
              />
            )}
          </FeatureCardProvider>
        </Card>
      )}
      {selectFeaturesStatisticsActive && <LayerStatistics onCount={setStatCount} />}
    </CardsContainer>
  );
});
